import * as React from 'react'
import { useDispatch } from 'react-redux'

import { WebshopPackage } from '../../../schema/webshop-package'
import { addProductToBasket } from '../../../state/actions/webshop/webshop'
import { useAppSelector } from '../../../state/reducers/types'
import Approve from '../../../stories/atoms/Icons/General/Approve'
import { Action, Category } from '../../../util/analytics-data-attributes/analyticsAttributes'
import currencyFormatter from '../../../util/currency-formatter/currency-formatter'
import {
  Content,
  ContentWrapper,
  Descriptions,
  DesktopBuyProductButton,
  DesktopPrice,
  Header,
  Image,
  MobileBuyProductButton,
  MobileFooter,
  MobilePrice,
  ProductLink,
  StyledShoppingCardMessage,
  StyledShoppingCart,
  Title,
  Wrapper,
} from './product-card.style'
import styled from 'styled-components'

type Props = {
  product: WebshopPackage
  compact?: boolean /** Render components compact (price always on the bottom of the component) or normal */
}

const ProductCard: React.FunctionComponent<Props> = ({ product, compact = false }) => {
  const [pendingOrder, setPendingOrder] = React.useState<WebshopPackage>()
  const status = useAppSelector(state => state.webshop.status)
  const dispatch = useDispatch()
  const buttonIsDisabled = React.useMemo(
    () => status === 'pending' && pendingOrder?.productCode === product.productCode,
    [status, pendingOrder, product],
  )

  const onAddToBasket = event => {
    event.preventDefault()
    setPendingOrder(product)
    dispatch(addProductToBasket(product))
  }

  React.useEffect(() => {
    if (status !== 'pending' && pendingOrder) {
      setPendingOrder(null)
    }
  }, [status])

  const price = currencyFormatter(product.price, false)

  return (
    <ProductLink href={`/webshop/products/${product.productCode}`} data-testid="productsListCard">
      <Wrapper>
        <Image compact={compact}>
          <img
            data-testid="productCardImage"
            src={product.listImage ? product.listImage : 'https://via.placeholder.com/160x120'}
            alt={product.name}
          />
        </Image>

        <ContentWrapper>
          <Content>
            <Header>
              <Title>{product.name}</Title>
              {!compact && <DesktopPrice>{price}</DesktopPrice>}
            </Header>

            <div>
              {product.descriptions.length > 0 && (
                <Descriptions data-testid="descriptions">
                  {product.descriptions.map(description => (
                    <li key={description} className="checked">
                      <Approve />
                      {description}
                    </li>
                  ))}
                </Descriptions>
              )}
            </div>
          </Content>

          {!compact && (
            <DesktopBuyProductButton
              onClick={onAddToBasket}
              isInProgress={buttonIsDisabled}
              disabled={buttonIsDisabled}
              dataAnalyticsCategory={Category.ecommerce}
              dataAnalyticsAction={Action.addToCart}
              dataAnalyticsLabel={product.name}
            >
              <StyledShoppingCart /><StyledShoppingCardMessage>In winkelwagen</StyledShoppingCardMessage>
            </DesktopBuyProductButton>
          )}
        </ContentWrapper>
      </Wrapper>

      <MobileFooter alwaysShown={compact}>
        <MobilePrice>{price}</MobilePrice>
        <MobileBuyProductButton
          onClick={onAddToBasket}
          isInProgress={buttonIsDisabled}
          disabled={buttonIsDisabled}
          dataAnalyticsCategory={Category.ecommerce}
          dataAnalyticsAction={Action.addToCart}
          dataAnalyticsLabel={product.name}
        >
          <StyledShoppingCart /><StyledShoppingCardMessage>In winkelwagen</StyledShoppingCardMessage>
        </MobileBuyProductButton>
      </MobileFooter>
    </ProductLink>
  )
}

export default ProductCard
